import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import SceneLayout from '@/components/scene/SceneLayout';
import PlanBay from '@/components/scene/PlanBay';
import SignupDialog from '@/components/SignupDialog';
import { toast } from 'sonner';
import { apiFetch } from '@/lib/api';
import { PublicPlan, formatPlanPrice } from '@/components/PlanSelector';
import { billingIntervalLabel } from '@/lib/plans';
import { formatCredits } from '@/lib/creditCosts';

export default function Pricing() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [plans, setPlans] = useState<PublicPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [authOpen, setAuthOpen] = useState(false);
  const [pendingPlan, setPendingPlan] = useState<PublicPlan | null>(null);
  // Plan under the pointer/focus, mirrored as a column highlight in the credits schedule.
  const [focusKey, setFocusKey] = useState<string | null>(null);

  // Pricing is fully DB-driven so it always reflects what super-admins publish.
  useEffect(() => {
    let active = true;
    apiFetch<{ plans: PublicPlan[] }>('/plans')
      .then((r) => { if (active) setPlans(r.plans || []); })
      .catch((e) => { if (active) toast.error(e instanceof Error ? e.message : 'Failed to load plans'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const billingCycleLabel = (interval: string) => billingIntervalLabel(interval, t);

  // Redirect straight to Stripe — no intermediate plan-picker page.
  const startCheckout = async (workspaceId: string, planKey: string) => {
    setBusyKey(planKey);
    try {
      const res = await apiFetch<{ checkoutUrl: string }>(`/workspaces/${workspaceId}/billing/checkout`, {
        method: 'POST',
        body: JSON.stringify({ planKey, topUpCredits: 0, acceptTerms: true }),
      });
      window.location.href = res.checkoutUrl;
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Checkout failed');
      setBusyKey(null);
    }
  };

  // For an already-signed-in user: use their workspace (or create one if missing).
  const resolveWorkspaceId = async (): Promise<string> => {
    const stored = localStorage.getItem('workspaceId');
    if (stored) return stored;
    const { workspaces } = await apiFetch<{ workspaces: { id: string }[] }>('/workspaces');
    if (workspaces && workspaces.length > 0) {
      localStorage.setItem('workspaceId', workspaces[0].id);
      return workspaces[0].id;
    }
    const created = await apiFetch<{ workspace: { id: string } }>('/workspaces', {
      method: 'POST',
      body: JSON.stringify({ name: 'My Workspace' }),
    });
    localStorage.setItem('workspaceId', created.workspace.id);
    return created.workspace.id;
  };

  const handleSelectPlan = async (plan: PublicPlan) => {
    // Custom / "contact us" plans (no price) -> talk to sales.
    if (plan.price == null) {
      toast.success(t('pricing.contact_sales_message'));
      navigate('/support');
      return;
    }

    // Not signed in -> inline signup popup (account + workspace), then straight to payment.
    if (!localStorage.getItem('token')) {
      setPendingPlan(plan);
      setAuthOpen(true);
      return;
    }

    try {
      setBusyKey(plan.key);
      const workspaceId = await resolveWorkspaceId();
      await startCheckout(workspaceId, plan.key);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not start checkout');
      setBusyKey(null);
    }
  };

  const rate = (value: number | null | undefined) =>
    value == null ? t('pricing.custom_rate') : formatCredits(value);

  const scheduleRows: { label: string; value: (p: PublicPlan) => string }[] = [
    { label: `${t('pricing.whatsapp')} · ${t('pricing.init')}`, value: (p) => rate(p.whatsappInitCredits) },
    { label: `${t('pricing.whatsapp')} · ${t('pricing.next')}`, value: (p) => rate(p.whatsappNextCredits) },
    { label: t('pricing.sms'), value: (p) => rate(p.smsCredits) },
    { label: t('pricing.billing_cycle'), value: (p) => billingCycleLabel(p.interval) },
    { label: t('pricing.price'), value: (p) => (p.price == null ? t('pricing.custom_rate') : formatPlanPrice(p)) },
  ];

  return (
    <SceneLayout>
      {/* Title — the drawing's title block */}
      <section className="wrap pt-[clamp(48px,10vh,120px)] pb-[clamp(40px,7vh,88px)]" data-rv="dissolve">
        <div className="flex flex-wrap items-end justify-between gap-8">
          <div className="grid gap-5">
            <p className="mono">{t('pricing.badge')}</p>
            <h1 className="display max-w-[14ch]">{t('pricing.title')}</h1>
            <p className="body max-w-[46ch]">{t('pricing.subtitle')}</p>
          </div>
          <p className="mono" aria-hidden="true">A-201 · 1:100</p>
        </div>
      </section>

      {/* The elevation — one building, many bays */}
      <section className="wrap">
        {loading ? (
          <div className="flex justify-center py-24">
            <Loader2 className="h-6 w-6 animate-spin text-[var(--graphite)]" />
          </div>
        ) : plans.length === 0 ? (
          <p className="body py-16">{t('pricing.subtitle')}</p>
        ) : (
          <div data-rv="aperture">
            <div className="elev__roof" aria-hidden="true" />
            <div className="elev" style={{ ['--bays' as string]: Math.min(plans.length, 5) }}>
              {plans.map((plan, i) => (
                <PlanBay
                  key={plan.key}
                  plan={plan}
                  index={i}
                  billingCycle={billingCycleLabel(plan.interval)}
                  busy={busyKey === plan.key}
                  onSelect={() => handleSelectPlan(plan)}
                  onFocusChange={setFocusKey}
                />
              ))}
            </div>
            <div className="elev__ground" aria-hidden="true" />
          </div>
        )}
      </section>

      {/* Credits schedule */}
      {plans.length > 0 && (
        <section className="wrap section">
          <div className="split-golden">
            <div className="grid gap-4 content-start">
              <p className="mono">S-02</p>
              <h2 className="display-m">{t('pricing.schedule_title')}</h2>
              <p className="body max-w-[34ch]">{t('pricing.schedule_sub')}</p>
            </div>
            <div className="sched" data-rv="line">
              <table>
                <thead>
                  <tr>
                    <th scope="col" className="mono">{t('pricing.schedule_item')}</th>
                    {plans.map((p) => (
                      <th key={p.key} scope="col" data-col-active={focusKey === p.key}>{p.name}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {scheduleRows.map((row) => (
                    <tr key={row.label}>
                      <th scope="row" className="font-normal text-[var(--graphite)]">{row.label}</th>
                      {plans.map((p) => (
                        <td key={p.key} data-col-active={focusKey === p.key}>{row.value(p)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}

      {/* Margin notes on the drawing */}
      <section className="wrap pb-[var(--section)]">
        <div className="grid md:grid-cols-2 gap-12 md:gap-20 max-w-[1040px]" data-rv="rack">
          <div className="note grid gap-3 content-start">
            <p className="mono">N1</p>
            <h2 className="text-[1.35rem] font-medium">{t('pricing.credit_title')}</h2>
            <p className="body text-[15px]">{t('pricing.credit_copy')}</p>
          </div>
          <div className="note grid gap-3 content-start">
            <p className="mono">N2</p>
            <h2 className="text-[1.35rem] font-medium">{t('pricing.checkout_title')}</h2>
            <p className="body text-[15px]">{t('pricing.checkout_copy')}</p>
          </div>
        </div>
      </section>

      <SignupDialog
        open={authOpen}
        onOpenChange={setAuthOpen}
        onComplete={(workspaceId) => {
          setAuthOpen(false);
          if (pendingPlan) startCheckout(workspaceId, pendingPlan.key);
        }}
      />
    </SceneLayout>
  );
}
